import type { DriverActivity, GeofenceEvent, Shift, Transport } from "@ots-frontend/ots-data";
import type { ActivityEntry } from "./activity-types";

/**
 * The rules WaterGo's Driver Activity applies before splitting or merging records, as pure functions of the
 * selection (and, where order matters, the day's rows): each returns what to send, or undefined when it does not
 * apply, so the actions can be disabled rather than failing.
 */

/** A pickup and a delivery to pair: the pickup from one row, the delivery from the other, unmatched ones first. */
export const associatePair = (entries: ActivityEntry[]): { pickupTransportId: string; deliveryTransportId: string } | undefined => {
  if (entries.length !== 2) return undefined;
  const [first, second] = entries;
  const options = [[first, second], [second, first]]
    .filter(([from, to]) => from.pickup?.id && to.delivery?.id && from.pickup.id !== to.delivery.id)
    // A lone pickup and a lone delivery are what pairing is for; taking a side off a ticket comes second.
    .sort(([fromA, toA], [fromB, toB]) =>
      Number(fromB.kind === "pickup") + Number(toB.kind === "delivery") - Number(fromA.kind === "pickup") - Number(toA.kind === "delivery"));
  const best = options[0];
  return best && { pickupTransportId: best[0].pickup!.id, deliveryTransportId: best[1].delivery!.id };
};

/** The transport and geofence visit to join: one of each selected, the transport at the visit's site or a sister site. */
export const geofenceMergeTarget = (entries: ActivityEntry[]): { transport: Transport; visit: GeofenceEvent } | undefined => {
  if (entries.length !== 2) return undefined;
  const visitEntry = entries.find((entry) => entry.geofenceEvent && !entry.pickup && !entry.delivery);
  const other = entries.find((entry) => entry !== visitEntry);
  const visit = visitEntry?.geofenceEvent;
  if (!visit || !other) return undefined;
  const atVisit = (transport: Transport | undefined) => !!transport && (
    (!!transport.siteId && transport.siteId === visit.siteId)
    || (!!transport.siteGeofenceSiteGroupId && transport.siteGeofenceSiteGroupId === visit.geofenceSiteGroupId));
  const transport = [other.pickup, other.delivery].find(atVisit);
  return transport && { transport, visit };
};

/**
 * What changes to put a transport in a geofence visit, as WaterGo does: an open visit that began before the ticket
 * is closed at the ticket (or its time out); otherwise the ticket moves inside the visit, a pickup just after it
 * began and a delivery just before it ended.
 */
export const geofenceReplacement = (transport: Transport, visit: GeofenceEvent):
  { kind: "visit"; visit: GeofenceEvent } | { kind: "transport"; transport: Transport } => {
  const created = transport.createDttm ?? 0;
  if (visit.outDttm == null && visit.inDttm <= created) {
    const out = transport.outDttm != null && transport.outDttm >= created ? transport.outDttm : created;
    return { kind: "visit", visit: { ...visit, outDttm: out } };
  }
  const createDttm = transport.type === "PICKUP" ? visit.inDttm + 1_000 : (visit.outDttm ?? visit.inDttm) - 1_000;
  return { kind: "transport", transport: { ...transport, createDttm } };
};

const driverOf = (row: DriverActivity) => row.startShift?.personId ?? row.endShift?.personId ?? row.geofenceEvent?.personId;

/** The day's shifts for a driver, by start: only these can be merged, and only back to back. */
const shiftsOf = (rows: DriverActivity[], personId: string) =>
  [...new Map(rows.map((row) => row.startShift).filter((shift): shift is Shift => shift?.personId === personId).map((shift) => [shift.id, shift])).values()]
    .sort((left, right) => left.startDttm - right.startDttm);

/** The shifts of the selected rows when they are a driver's two or more back-to-back shifts. */
export const shiftsToMerge = (entries: ActivityEntry[], rows: DriverActivity[]): Shift[] | undefined => {
  const shifts = [...new Map(entries.map((entry) => entry.shift).filter((shift): shift is Shift => !!shift).map((shift) => [shift.id, shift])).values()];
  if (shifts.length < 2 || new Set(shifts.map((shift) => shift.personId)).size !== 1) return undefined;
  const all = shiftsOf(rows, shifts[0].personId);
  const positions = shifts.map((shift) => all.findIndex((candidate) => candidate.id === shift.id)).sort((left, right) => left - right);
  // A shift of the driver's between them would be swallowed, so they must follow one another.
  if (positions.some((position) => position < 0) || positions[positions.length - 1] - positions[0] !== positions.length - 1) return undefined;
  return positions.map((position) => all[position]);
};

/** The selected geofence visits when they are a driver's two or more back-to-back visits. */
export const visitsToMerge = (entries: ActivityEntry[], rows: DriverActivity[]): GeofenceEvent[] | undefined => {
  const visits = entries.map((entry) => entry.geofenceEvent).filter((visit): visit is GeofenceEvent => !!visit);
  if (visits.length < 2 || visits.length !== entries.length || new Set(visits.map((visit) => visit.personId)).size !== 1) return undefined;
  const all = rows.map((row) => row.geofenceEvent).filter((visit): visit is GeofenceEvent => !!visit && driverOf({ geofenceEvent: visit }) === visits[0].personId)
    .sort((left, right) => left.inDttm - right.inDttm);
  const positions = visits.map((visit) => all.findIndex((candidate) => candidate.id === visit.id)).sort((left, right) => left - right);
  if (positions.some((position) => position < 0) || positions[positions.length - 1] - positions[0] !== positions.length - 1) return undefined;
  return positions.map((position) => all[position]);
};

/** The sites of visits to merge, when they differ and someone has to pick the merged visit's site. */
export const visitSites = (visits: GeofenceEvent[]) =>
  [...new Map(visits.filter((visit) => visit.siteId).map((visit) => [visit.siteId!, visit.siteName ?? visit.siteId!])).entries()]
    .map(([siteId, siteName]) => ({ siteId, siteName }));

/** Tickets on the selection, for the transport splits. */
export const selectedTickets = (entries: ActivityEntry[]) => entries.flatMap((entry) => entry.tta ? [entry.tta] : []);

/** Splitting evenly divides pay, so every ticket has to have been costed (it has a previous transport time). */
export const evenSplitTickets = (entries: ActivityEntry[]) => {
  const tickets = selectedTickets(entries);
  return tickets.length > 0 && tickets.length === entries.length && tickets.every((tta) => tta.prevTransportDttm != null) ? tickets : undefined;
};
